import '@typeform/embed/build/css/popup.css'
import { createPopup } from '@typeform/embed'
import { useEffect, useRef } from 'react'

import { getEnvVar } from 'utils/env'
import { getCookie, setCookie } from 'utils/storage'

export type TypeformSurvey = 'break' | 'cancel' | 'postPurchase' | 'skip'

const ENABLE_SANDBOX = getEnvVar('APP_ENV') !== 'production'

const SURVEY_NAME_TO_FORM_ID: {
  [survey in TypeformSurvey]: string
} = {
  break: 'vveRycjF',
  cancel: 'b0XJUW',
  postPurchase: 'nSgG9qUa',
  skip: 'GBOKHQ',
}

const SURVEY_SOURCE = 'web'

export function useTypeformPopup({ userID }: { userID: string }) {
  const popup = useRef<ReturnType<typeof createPopup> | null>(null)

  useEffect(() => {
    return () => {
      popup.current?.close()
      popup.current?.unmount()
    }
  }, [])

  return {
    openTypeformPopup: ({
      hiddenFields,
      onClose,
      onReady,
      onSubmit,
      survey,
    }: {
      hiddenFields?: Record<string, string>
      onClose?: () => void
      onReady?: () => void
      onSubmit?: () => void
      survey: TypeformSurvey
    }) => {
      if (
        (survey === 'break' || survey === 'cancel') &&
        getSurveyDisplayed(survey)
      ) {
        onClose?.()
        return
      }

      if (survey === 'break' || survey === 'cancel') {
        storeSurveyDisplayed(survey)
      }

      const formID = survey ? SURVEY_NAME_TO_FORM_ID[survey] : undefined

      if (formID) {
        const hidden = {
          source: SURVEY_SOURCE,
          userid: userID,
          ...hiddenFields,
        }

        popup.current = createPopup(formID, {
          autoClose: 2000,
          enableSandbox: ENABLE_SANDBOX,
          hidden,
          hideFooter: true,
          hideHeaders: true,
          onClose,
          onReady,
          onSubmit,
        })
      }

      popup.current?.open()
    },
  }
}

const SURVEY_DISPLAYED_COOKIE_NAME = {
  break: 'BREAK_SURVEY_DISPLAYED' as const,
  cancel: 'CANCEL_SURVEY_DISPLAYED' as const,
}

// Don't show the break or cancel survey again within 30 minutes of the last time it was displayed
// This is to prevent the same user from being shown the same survey multiple times in a short period of time
function storeSurveyDisplayed(survey: 'break' | 'cancel') {
  const expires = new Date()
  const currentMinutes = expires.getUTCMinutes()
  expires.setUTCMinutes(currentMinutes + 30)

  setCookie({
    cookieName: SURVEY_DISPLAYED_COOKIE_NAME[survey],
    content: 'true',
    options: {
      expires,
    },
  })
}

function getSurveyDisplayed(survey: 'break' | 'cancel') {
  return getCookie(SURVEY_DISPLAYED_COOKIE_NAME[survey])
}
